"use client";

import clsx from "clsx";
import { useMemo, useState } from "react";

import { Icon, type IconNameExtended } from "@/components/library/Icon";

export type IconOption = { label: string; value: string };

export function IconPickerField({
  value,
  onChange,
  options,
}: Readonly<{
  value: string;
  onChange: (value: string) => void;
  options: IconOption[];
}>) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        option.value.toLowerCase().includes(term),
    );
  }, [query, options]);

  const selected = options.find((option) => option.value === value);

  return (
    <div className="flex flex-col gap-2">
      <input
        type="text"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={selected ? `Selected: ${selected.label}` : "Search icons…"}
        className="w-full rounded-md border border-black/15 px-2 py-1.5 text-sm outline-none focus:border-black/40"
      />
      <div className="grid max-h-48 grid-cols-5 gap-1 overflow-y-auto rounded-md border border-black/10 p-1">
        {filtered.map((option) => {
          const isSelected = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              title={`${option.label} (${option.value})`}
              aria-label={option.label}
              aria-pressed={isSelected}
              onClick={() => onChange(option.value)}
              className={clsx(
                "flex aspect-square items-center justify-center rounded-md border transition-colors",
                isSelected
                  ? "border-gail-primary bg-gail-primary/10 text-gail-primary"
                  : "border-transparent text-black/70 hover:bg-black/5",
              )}
            >
              <Icon
                icon={option.value as IconNameExtended}
                className="h-4 w-4"
              />
            </button>
          );
        })}
        {filtered.length === 0 ? (
          <p className="col-span-5 px-1 py-2 text-xs text-black/50">
            No icons match “{query.trim()}”.
          </p>
        ) : null}
      </div>
    </div>
  );
}
